import {
    Alert,
    AlertTitle,
    Box,
    CardHeader,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    TextField,
    Tooltip,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { Controller, UseFormReturn } from "react-hook-form";
import { impactLevelInfo, RiskLevel, riskLevels } from "../../../app/riskLevel";
import {
    ALLOW_CROSSINGS_CANADA,
    ALLOW_CROSSINGS_MEXICO,
    ALLOW_CROSSINGS_US,
    AVOID_TOLLS,
    DAILY_END_TIME,
    DAILY_END_TIME_TOOLTIP,
    DAILY_FINAL_END_TIME,
    DAILY_FINAL_END_TIME_TOOLTIP,
    DAILY_START_TIME,
    DAILY_START_TIME_TOOLTIP,
    FEET,
    GOLDILOCKS,
    HEIGHT_INPUT_TOOLTIP,
    HEIGHT_TOOLTIP,
    IMPACT_LEVEL_WARNING,
    INCHES,
    LEVEL_FIVE,
    LEVEL_FOUR,
    MAX_SPEED,
    MAX_SPEED_TOOLTIP,
    MAX_WEATHER_IMPACT,
    MAX_WETAHER_IMPACT_TOOLTIP,
    OTHER_MAX_SPEED,
    UNRECOGNIZED,
    WEIGHT_INPUT_TOOLTIP,
    WEIGHT_TOOLTIP,
} from "../../UserVisibleStrings";
import { BorderWithLabel } from "./BorderWithLabel";
import { useErrorProps, useMuiFieldRegister } from "./TripForm";
import {
    ABSOLUTE_MAX_SPEED,
    assessCountries,
    CountryAssessment,
    DEFAULT_MAX_SPEED,
    RouteRequestFormData,
} from "./TripFormTypeConversions";
import React, { useEffect, useMemo } from "react";
import { getTripDrawerWidth } from "../../tripSidebar/TripDrawer";
import { useIsMobile } from "../../../features/pageWidth/pageWidthSlice";
import useIdentity from "../../../app/auth";
import ImpactIcon from "../../risk/ImpactIcon";
import { useUserViewOverride } from "../../../features/riskViewer/userViewOverrideSlice";

type TripOptionsFormProps = {
    formParent: UseFormReturn<RouteRequestFormData>,
    isAdvisory: boolean,
    backgroundColor: string,
};

export function TripOptionsForm( {
    formParent,
    isAdvisory,
    backgroundColor
}: TripOptionsFormProps )
{
    const { formState, register, control, watch, setValue } = formParent;
    const { errors } = formState;

    const userViewOverride = useUserViewOverride();
    const { isAdmin } = useIdentity( userViewOverride );
    const muiTextFieldRegister = useMuiFieldRegister( register, errors );
    const errorProps = useErrorProps( errors );
    const isMobile = useIsMobile();

    const watchOverrideSpeed = watch( "overrideMaxSpeed" );
    const waypoints = watch( "waypoints" );
    const countryToggles: CountryAssessment = useMemo( () =>
    {
        return assessCountries( waypoints ?? [], false, false, false );
    }, [ waypoints ] );
    useEffect( () =>
    {
        if ( countryToggles.includeCanada )
        {
            setValue( "includeCanada", true );
        }
    }, [ countryToggles.includeCanada, setValue ] );

    useEffect( () =>
    {
        if ( countryToggles.includeMexico )
        {
            setValue( "includeMexico", true );
        }
    }, [ countryToggles.includeMexico, setValue ] );

    useEffect( () =>
    {
        if ( countryToggles.includeUs )
        {
            setValue( "includeUS", true );
        }
    }, [ countryToggles.includeUs, setValue ] );

    return (
        <Box
            sx = {{
                display: "grid",
                flexDirection: "column",
                gap: 3,
                alignItems: "start",
            }}
        >
            <Controller
                control = {control}
                name = "dailyStartTime"
                rules = {{ deps: [ "dailyDesiredEndTime", "dailyFinalEndTime" ] }}
                render = {( { field } ) => (
                    <FormControl>
                        <TimePicker
                            label = {
                                <Tooltip title = {DAILY_START_TIME_TOOLTIP}>
                                    <Box>{DAILY_START_TIME} </Box>
                                </Tooltip>
                            }
                            value = {DateTime.fromJSDate( field.value )}
                            onChange = {( value: DateTime | null | undefined ) =>
                            {
                                if ( value === null )
                                {
                                    field.onChange( undefined );
                                    return;
                                }
                                formParent.clearErrors( "startDate" ); // reset any errors if you update the waypoint time
                                field.onChange( value?.toJSDate() );
                            }}
                            slotProps = {{
                                textField: {
                                    ...errorProps( field.name ),
                                },
                            }}
                        />
                    </FormControl>
                )}
            />
            {!isAdvisory &&
                <Controller
                    control = {control}
                    name = "dailyDesiredEndTime"
                    rules = {{ deps: [ "dailyStartTime", "dailyFinalEndTime" ] }}
                    render = {( { field } ) => (
                        <FormControl>
                            <TimePicker
                                label = {
                                    <Tooltip title = {DAILY_END_TIME_TOOLTIP}>
                                        <Box>{DAILY_END_TIME} </Box>
                                    </Tooltip>
                                }
                                value = {DateTime.fromJSDate( field.value )}
                                onChange = {( value: DateTime | null | undefined ) =>
                                {
                                    if ( value === null )
                                    {
                                        field.onChange( undefined );
                                        return;
                                    }
                                    field.onChange( value?.toJSDate() );
                                }}
                                slotProps = {{
                                    textField: {
                                        ...errorProps( field.name ),
                                    },
                                }}
                            />
                        </FormControl>
                    )}
                />}
            {!isAdvisory &&
                <Controller
                    control = {control}
                    name = "dailyFinalEndTime"
                    rules = {{ deps: [ "dailyDesiredEndTime", "dailyStartTime" ] }}
                    render = {( { field } ) => (
                        <FormControl>
                            <TimePicker
                                label = {
                                    <Tooltip title = {DAILY_FINAL_END_TIME_TOOLTIP}>
                                        <Box>{DAILY_FINAL_END_TIME} </Box>
                                    </Tooltip>
                                }
                                value = {DateTime.fromJSDate( field.value )}
                                onChange = {( value: DateTime | null | undefined ) =>
                                {
                                    if ( value === null )
                                    {
                                        field.onChange( undefined );
                                        return;
                                    }
                                    field.onChange( value?.toJSDate() );
                                }}
                                slotProps = {{
                                    textField: {
                                        ...errorProps( field.name ),
                                    },
                                }}
                            />
                        </FormControl>
                    )}
                />}
            {!isAdvisory &&
            <Controller
                control = {control}
                name = "maxRiskLevel"
                render = {( { field } ) => (
                    <FormControl>
                        <Tooltip title = {MAX_WETAHER_IMPACT_TOOLTIP}>
                            <InputLabel id = "maxRiskLevel">{MAX_WEATHER_IMPACT}</InputLabel>
                        </Tooltip>
                        <Select
                            labelId = "maxRiskLevel"
                            label = {MAX_WEATHER_IMPACT}
                            value = {field.value}
                            onChange = {( e ) => field.onChange( e.target.value as RiskLevel )}
                            inputProps = {{ onBlur: field.onBlur }}
                        >
                            {riskLevels.filter( each => each !== GOLDILOCKS && each !== UNRECOGNIZED ).map( ( level ) => (
                                <MenuItem key = {level} value = {level}>
                                    <Tooltip title = {impactLevelInfo[level].tooltip}>
                                        <Box
                                            sx = {{
                                                display: "flex",
                                                flexWrap: "nowrap",
                                                flexGrow: 1,
                                                gap: 2,
                                            }}
                                        >
                                            <ImpactIcon level = {level} width = {24}/>
                                            <Box>{level}</Box>
                                        </Box>
                                    </Tooltip>
                                </MenuItem>
                            ) )}
                        </Select>
                        {( field.value === LEVEL_FOUR || field.value === LEVEL_FIVE ) &&
                        <Alert severity = "warning">
                            <AlertTitle>Warning</AlertTitle>
                            {IMPACT_LEVEL_WARNING}
                        </Alert>}
                    </FormControl>
                )}
            />}

            <BorderWithLabel
                rows = {formParent.getValues().overrideMaxSpeed ? 2 : 1}
                label = {MAX_SPEED}
                errorProp = {errorProps( "maxDrivingSpeed" )}
                topOffset = {-25}
                toolTipText = {MAX_SPEED_TOOLTIP}
                overrideIsSelected = {false}
                backgroundColor = {backgroundColor}
            >
                <Grid container spacing = {2} sx = {{ padding: "10px" }}>
                    <Grid item xs = {12}>
                        <Controller
                            control = {control}
                            name = "overrideMaxSpeed"
                            render = {( { field } ) => (
                                <FormControlLabel
                                    label = {OTHER_MAX_SPEED}
                                    control = {
                                        <Checkbox
                                            checked = {field.value}
                                            onChange = {( e, newValue ) =>
                                            {
                                                field.onChange( newValue );
                                            }}
                                        />
                                    }
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs = {12} sx = {{ width: "100%" }}>
                        <Controller
                            control = {control}
                            name = "maxDrivingSpeed"
                            render = {( { field } ) => watchOverrideSpeed ? (
                                <FormControl>
                                    <Slider
                                        sx = {{ width: isMobile ? "280px" : `calc(${getTripDrawerWidth( isMobile )} - 120px)`, align: "center" }}
                                        // TODO: need to show error color
                                        aria-label = {MAX_SPEED}
                                        defaultValue = {DEFAULT_MAX_SPEED}
                                        min = {45}
                                        max = {ABSOLUTE_MAX_SPEED}
                                        value = {field.value}
                                        valueLabelDisplay = "auto"
                                        valueLabelFormat = {( value ) => <div>{value} MPH</div>}
                                        onChange = {( e, newValue ) =>
                                        {
                                            field.onChange( newValue );
                                        }}
                                        onBlur = {field.onBlur}
                                    />
                                </FormControl>
                            ) : <div/>}
                        />
                    </Grid>
                </Grid>
            </BorderWithLabel>
            <React.Fragment>
                {isAdmin &&
                <TextField
                    label = {
                        <Tooltip title = {WEIGHT_INPUT_TOOLTIP}>
                            <Box>{WEIGHT_TOOLTIP}</Box>
                        </Tooltip>
                    }
                    InputLabelProps = {{
                        shrink: true,
                    }}
                    sx = {{ flexGrow: 1 }}
                    inputMode = "numeric"
                    InputProps = {{
                        endAdornment: <InputAdornment position = "end" disablePointerEvents>Pounds</InputAdornment>,
                    }}
                    {...muiTextFieldRegister( "weightPounds", {
                        setValueAs: ( value: string ) => value === "" ? undefined : Number( value )
                    } )}
                />}
                <Box display = "flex" justifyContent = "space-between">
                    <CardHeader sx = {{ padding: "0px 5px" }}
                        subheader = {
                            <Tooltip title = {HEIGHT_INPUT_TOOLTIP}>
                                <Box>{HEIGHT_TOOLTIP}</Box>
                            </Tooltip>
                        }
                    />
                    <TextField
                        sx = {{ flexGrow: 1, marginRight: 2 }}
                        inputMode = "numeric"
                        InputProps = {{
                            endAdornment: <InputAdornment position = "end" disablePointerEvents>{FEET}</InputAdornment>,
                        }}
                        {...muiTextFieldRegister( "heightFeet", {
                            setValueAs: ( value: string ) => value === "" ? undefined : Number( value ),
                            deps: [ "heightInches" ]
                        } )}
                    />
                    <TextField
                        sx = {{ flexGrow: 1 }}
                        inputMode = "numeric"
                        InputProps = {{
                            endAdornment: <InputAdornment position = "end" disablePointerEvents>{INCHES}</InputAdornment>,
                        }}
                        {...muiTextFieldRegister( "heightInches", {
                            setValueAs: ( value: string ) => value === "" ? undefined : Number( value ),
                            deps: [ "heightFeet" ]
                        } )}
                    />
                </Box>
            </React.Fragment>
            <Box display = "flex" justifyContent = "space-between" alignItems = "center">

                {/* Avoid Column */}
                <Box flexGrow = {1} p = {2} border = {1} borderRadius = {1}>
                    <Controller
                        control = {control}
                        name = "avoidTolls"
                        render = {( { field } ) => (
                            <FormControlLabel
                                label = {AVOID_TOLLS}
                                control = {
                                    <Checkbox
                                        checked = {field.value}
                                        onChange = {( e, newValue ) =>
                                        {
                                            field.onChange( newValue );
                                        }}
                                    />
                                }
                            />
                        )}
                    />
                    <Controller
                        control = {control}
                        name = "includeUS"
                        render = {( { field } ) => (
                            <FormControlLabel
                                label = {ALLOW_CROSSINGS_US}
                                control = {
                                    <Checkbox
                                        checked = {field.value}
                                        onChange = {( e, newValue ) => field.onChange( newValue )}
                                        disabled = {countryToggles.includeUs} // Disable if auto-included
                                    />
                                }
                            />
                        )}
                    />
                    <Controller
                        control = {control}
                        name = "includeMexico"
                        render = {( { field } ) => (
                            <FormControlLabel
                                label = {ALLOW_CROSSINGS_MEXICO}
                                control = {
                                    <Checkbox
                                        checked = {field.value}
                                        onChange = {( e, newValue ) => field.onChange( newValue )}
                                        disabled = {countryToggles.includeMexico} // Disable if auto-included
                                    />
                                }
                            />
                        )}
                    />
                    <Controller
                        control = {control}
                        name = "includeCanada"
                        render = {( { field } ) => (
                            <FormControlLabel
                                label = {ALLOW_CROSSINGS_CANADA}
                                control = {
                                    <Checkbox
                                        checked = {field.value}
                                        onChange = {( e, newValue ) => field.onChange( newValue )}
                                        disabled = {countryToggles.includeCanada} // Disable if auto-included
                                    />
                                }
                            />
                        )}
                    />
                </Box>
            </Box>
        </Box>
    );
}
